import React, { useState, useCallback, useEffect } from 'react';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { pickBy } from 'lodash';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';

import Badge from 'modules/common/components/Badge';
import Pagination from 'partial/components/Pagination';
import Table from 'partial/components/Table';
import ListPanel from 'partial/components/ListPanel';
import Button from 'partial/components/Button';
import SearchBar from 'partial/components/input/SearchBar';
import { useCreateModal } from 'partial/components/Modal';
import AddUpdate from './modals/AddUpdate';
import Delete from './modals/Delete';
import { useBannedWords } from './hooks';

function BannedWords() {
  const { setSubLabel } = useModuleContext();

  const createModal = useCreateModal();
  const [filter, setFilter] = useState({ q: '' });
  const [isLoading, list, meta] = useBannedWords(pickBy(filter));
  const addModal = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdate onClose={onClose} />,
    });
  }, [createModal]);

  const editModal = useCallback(
    (data) => {
      createModal({
        content: (onClose) => (
          <AddUpdate data={data} onClose={onClose} type="edit" />
        ),
      });
    },
    [createModal],
  );

  const deleteModal = useCallback(
    (data) => {
      createModal({
        content: (onClose) => <Delete data={data} onClose={onClose} />,
      });
    },
    [createModal],
  );

  const renderAction = (data) => (
    <div className="flex items-center justify-end gap-2">
      <Button outline size="sm" icon={Trash} onClick={() => deleteModal(data)}>
        Delete
      </Button>
      <Button
        outline
        size="sm"
        icon={PencilSimple}
        onClick={() => editModal(data)}
      >
        Edit
      </Button>
    </div>
  );

  const format = [
    {
      label: 'STATUS',
      contentClassName: 'w-[12rem]',
      key: (data) => <Badge status={data?.status} />,
    },
    {
      label: 'WORDS',
      key: 'ban_word',
    },
    {
      label: 'ACTION',
      id: 'action',
      labelClassName: 'whitespace-nowrap',
      contentClassName: 'whitespace-nowrap',
      key: (data) => renderAction(data),
    },
  ];

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} banned word${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <ListPanel
      filters={
        <>
          <SearchBar name="q" value={filter.q} onChange={setFilter} />
          <Button
            primary
            icon={Plus}
            className="ml-auto"
            onClick={() => addModal()}
          >
            Add banned word
          </Button>
        </>
      }
      pagination={
        +meta?.last_page > 1 ? (
          <Pagination value={meta} onChange={setFilter} />
        ) : null
      }
    >
      <Table
        isLoading={isLoading}
        data={list}
        format={format}
        emptySpiel="No banned words match this search."
      />
    </ListPanel>
  );
}

export default BannedWords;
